import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #recommendationAbortController = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#recommendationAbortController?.abort();
    this.#recommendationAbortController = null;
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const addStartedInsideOpenDrawer =
      event.action === 'add' &&
      Boolean(this.#themeDrawer?.isOpen) &&
      event.target instanceof Element &&
      this.contains(event.target);

    const restoreDrawerFocus = () => {
      if (!addStartedInsideOpenDrawer || !this.#themeDrawer?.isOpen) return;

      // Cart hydration replaces the pressed quick-add button. If that leaves focus
      // on <body>, return it to a stable control inside the still-open modal.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (document.activeElement !== document.body) return;
          const closeButton = this.#dialog?.querySelector('.theme-drawer__close-button');
          if (closeButton instanceof HTMLElement) closeButton.focus();
        })
      );
    };

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (event.action === 'add' && !detail?.didError) {
          requestAnimationFrame(() => this.#loadContextualRecommendations(event, detail));
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          restoreDrawerFocus();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
          restoreDrawerFocus();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Resolves the exact product behind the successful add event. Product forms provide
   * productId directly; other add sources can be resolved by matching the added variant
   * against the refreshed Ajax cart response.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {any} detail
   * @returns {string | null}
   */
  #resolveJustAddedProductId(event, detail) {
    if (detail?.productId) return String(detail.productId);

    const items = Array.isArray(detail?.items) ? detail.items : [];
    const lines = Array.isArray(event.lines) ? [...event.lines].reverse() : [];
    for (const line of lines) {
      const item = items.find((candidate) => String(candidate.variant_id) === String(line.merchandiseId));
      if (item?.product_id) return String(item.product_id);
    }

    return null;
  }

  /**
   * Loads Shopify recommendations for the product just added. The server-side snippet
   * removes products already in the cart and renders only available products, so an empty
   * or unavailable result remains a safe hidden state rather than a generic fallback list.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {any} detail
   */
  async #loadContextualRecommendations(event, detail) {
    const target = this.querySelector('[data-k-cart-cross-sell]');
    if (!(target instanceof HTMLElement)) return;

    const productId = this.#resolveJustAddedProductId(event, detail);
    const { recommendationsUrl, sectionId, intent, limit } = target.dataset;
    if (!productId || !recommendationsUrl || !sectionId) {
      target.hidden = true;
      return;
    }

    this.#recommendationAbortController?.abort();
    const controller = new AbortController();
    this.#recommendationAbortController = controller;
    target.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(recommendationsUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('intent', intent || 'related');
      url.searchParams.set('limit', limit || '3');

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-k-cart-cross-sell]');
      const currentTarget = this.querySelector('[data-k-cart-cross-sell]');
      if (!(currentTarget instanceof HTMLElement)) return;

      if (replacement instanceof HTMLElement && replacement.querySelector('.k-cart-cross-sell__card')) {
        currentTarget.replaceWith(replacement);
      } else {
        currentTarget.replaceChildren();
        currentTarget.hidden = true;
        currentTarget.removeAttribute('aria-busy');
      }

      requestAnimationFrame(() => this.#updateStickyState());
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const currentTarget = this.querySelector('[data-k-cart-cross-sell]');
      if (currentTarget instanceof HTMLElement) {
        currentTarget.hidden = true;
        currentTarget.removeAttribute('aria-busy');
      }
      console.warn('[cart-drawer] Contextual recommendations unavailable:', error);
    } finally {
      if (this.#recommendationAbortController === controller) this.#recommendationAbortController = null;
    }
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
